import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, map } from "rxjs";
import {
  DemoFormViewModel,
  DomainViewModel,
  FilterViewModel,
  FunctionalityViewModel,
  GroupViewModel,
  IndustryViewModel,
  ProcessViewModel,
  AccountViewModel,
  UseCaseFormViewModel,
  UseCaseViewModel,
  VmImage,
  MacrosectorViewModel,
  ProcessFormDto
} from "../core/model/ContentManagement";
import { BrdLoggerService } from "@board/cloud-library/components/services";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";

interface MetadataHierarchyUpdate {
  macrosector?: MacrosectorViewModel;
  domain?: DomainViewModel;
  process?: ProcessViewModel;
  useCase?: UseCaseViewModel;
}

@Injectable({
  providedIn: "root"
})
export class MetadataService {
  controllerName = "Metadata";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;

  private functionalitiesSubject = new BehaviorSubject<FunctionalityViewModel[]>([]);
  functionalities$ = this.functionalitiesSubject.asObservable();

  private functionFilterSubject = new BehaviorSubject<number[]>([]);
  functionFilter$ = this.functionFilterSubject.asObservable();

  private groupsSubject = new BehaviorSubject<GroupViewModel[]>([]);
  groups$ = this.groupsSubject.asObservable();

  private useCasesSubject = new BehaviorSubject<UseCaseViewModel[]>([]);
  useCases$ = this.useCasesSubject.asObservable();

  private useCasesFilterSubject = new BehaviorSubject<number[]>([]);
  filterUseCases$ = this.useCasesFilterSubject.asObservable();

  private processesSubject = new BehaviorSubject<ProcessViewModel[]>([]);
  processes$ = this.processesSubject.asObservable();

  private domainSubject = new BehaviorSubject<DomainViewModel[]>([]);
  domains$ = this.domainSubject.asObservable();

  private macrosectorSubject = new BehaviorSubject<MacrosectorViewModel[]>([]);
  macrosectors$ = this.macrosectorSubject.asObservable();

  private industrySubject = new BehaviorSubject<IndustryViewModel[]>([]);
  industries$ = this.industrySubject.asObservable();

  private latestVersionSubject = new BehaviorSubject<VmImage | null>(null);
  latestVersion$ = this.latestVersionSubject.asObservable();

  private accountsSubject = new BehaviorSubject<AccountViewModel[]>([]);
  accounts$ = this.accountsSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private loggerService: BrdLoggerService
  ) {}

  getFunctinalities(): Observable<FunctionalityViewModel[]> {
    return this.httpClient.get<FunctionalityViewModel[]>(`${this.apiUrl}/functionality`);
  }

  getFormViewModel(): Observable<DemoFormViewModel> {
    return this.httpClient.get<DemoFormViewModel>(`${this.apiUrl}/form`);
  }

  getFilterViewModel(): Observable<FilterViewModel> {
    return this.httpClient.get<FilterViewModel>(`${this.apiUrl}/filter`);
  }

  getAccountsViewModel(): Observable<AccountViewModel[]> {
    return this.httpClient.get<AccountViewModel[]>(`${this.apiUrl}/accounts`).pipe(
      map(accounts => {
        this.setAccounts(accounts);
        return accounts;
      })
    );
  }

  setFunctinionalities(value: FunctionalityViewModel[]): void {
    this.functionalitiesSubject.next(value);
  }

  setFilterFunctionalities(value: number[]): void {
    this.functionFilterSubject.next(value);
  }

  setUseCases(value: UseCaseViewModel[]): void {
    this.useCasesSubject.next(value);
  }

  setFilterUseCases(value: number[]): void {
    this.useCasesFilterSubject.next(value);
  }

  setMacrosectos(value: MacrosectorViewModel[]): void {
    this.macrosectorSubject.next(value);
  }

  setDomains(value: DomainViewModel[]): void {
    this.domainSubject.next(value);
  }

  setProcesses(value: ProcessViewModel[]): void {
    this.processesSubject.next(value);
  }

  setGroups(value: GroupViewModel[]): void {
    this.groupsSubject.next(value);
  }

  setIndustries(value: IndustryViewModel[]): void {
    this.industrySubject.next(value);
  }

  setAccounts(value: AccountViewModel[]): void {
    this.accountsSubject.next(value);
  }

  addFunctionality(value: FunctionalityViewModel): Observable<FunctionalityViewModel> {
    return this.httpClient.post<FunctionalityViewModel>(`${this.apiUrl}/functionality`, value).pipe(
      map(res => {
        const currentFuncs = [...this.functionalitiesSubject.value];
        this.updateListValues(res, currentFuncs);
        this.functionalitiesSubject.next(currentFuncs);

        const currentGroups = [...this.groupsSubject.value];
        res.groups.forEach(group => {
          this.updateListValues(group, currentGroups);
        });
        this.groupsSubject.next(currentGroups);
        return res;
      })
    );
  }

  deleteFunctionality(ids: number[]): Observable<FunctionalityViewModel[]> {
    return this.httpClient.post<FunctionalityViewModel[]>(`${this.apiUrl}/functionality/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentFuncs = [...this.functionalitiesSubject.value];
        currentFuncs = currentFuncs.filter(x => !deletedIds.includes(x.id));
        this.functionalitiesSubject.next(currentFuncs);

        return res;
      })
    );
  }

  addUseCase(value: UseCaseFormViewModel): Observable<UseCaseViewModel> {
    return this.httpClient.post<UseCaseViewModel>(`${this.apiUrl}/usecase`, value).pipe(
      map(res => {
        const macrosectorId = res.macrosectorId || value.macrosectorId;
        const domainId = res.domainId || value.domainId;
        const processId = res.processId || value.processId;

        this.updateMetadata({
          macrosector: { id: macrosectorId, name: value.macrosectorName },
          domain: { id: domainId, name: value.domainName, macrosectorId },
          process: { id: processId, name: value.processName, domainId, macrosectorId },
          useCase: { id: res.id, name: res.name, processId, domainId, macrosectorId }
        });

        return res;
      })
    );
  }

  deleteUseCases(ids: number[]): Observable<UseCaseViewModel[]> {
    return this.httpClient.post<UseCaseViewModel[]>(`${this.apiUrl}/usecase/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.id));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addDomain(value: DomainViewModel): Observable<DomainViewModel> {
    return this.httpClient.post<DomainViewModel>(`${this.apiUrl}/domain`, value).pipe(
      map(res => {
        this.updateMetadata({
          domain: res
        });

        return res;
      })
    );
  }

  deleteDomains(ids: number[]): Observable<DomainViewModel[]> {
    return this.httpClient.post<DomainViewModel[]>(`${this.apiUrl}/domain/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentDomains = [...this.domainSubject.value];
        currentDomains = currentDomains.filter(x => !deletedIds.includes(x.id));
        this.domainSubject.next(currentDomains);

        let currentProcesses = [...this.processesSubject.value];
        currentProcesses = currentProcesses.filter(x => !deletedIds.includes(x.domainId));
        this.processesSubject.next(currentProcesses);

        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.domainId));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addProcess(value: ProcessFormDto): Observable<ProcessViewModel> {
    return this.httpClient.post<ProcessViewModel>(`${this.apiUrl}/process`, value).pipe(
      map(res => {
        const macrosectorId = res.macrosectorId || value.macrosectorId;
        const domainId = res.domainId || value.domainId;

        this.updateMetadata({
          macrosector: { id: macrosectorId, name: value.macrosectorName },
          domain: { id: domainId, name: value.domainName, macrosectorId },
          process: { id: res.id, name: res.name, domainId, macrosectorId }
        });

        return res;
      })
    );
  }

  deleteProcesses(ids: number[]): Observable<ProcessViewModel[]> {
    return this.httpClient.post<ProcessViewModel[]>(`${this.apiUrl}/process/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentProcesses = [...this.processesSubject.value];
        currentProcesses = currentProcesses.filter(x => !deletedIds.includes(x.id));
        this.processesSubject.next(currentProcesses);

        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.processId));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  addIndustry(value: IndustryViewModel): Observable<IndustryViewModel> {
    return this.httpClient.post<IndustryViewModel>(`${this.apiUrl}/industry`, value).pipe(
      map(res => {
        this.updateIndustries([res]);

        return res;
      })
    );
  }

  deleteIndustry(ids: number[]): Observable<IndustryViewModel[]> {
    return this.httpClient.post<IndustryViewModel[]>(`${this.apiUrl}/industry/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentIndustries = [...this.industrySubject.value];
        currentIndustries = currentIndustries.filter(x => !deletedIds.includes(x.id));
        this.industrySubject.next(currentIndustries);

        return res;
      })
    );
  }

  addGroup(value: GroupViewModel): Observable<GroupViewModel> {
    return this.httpClient.post<GroupViewModel>(`${this.apiUrl}/group`, value).pipe(
      map(res => {
        const currentGroups = [...this.groupsSubject.value];
        this.updateListValues(res, currentGroups);
        this.groupsSubject.next(currentGroups);

        return res;
      })
    );
  }

  deleteGroup(ids: number[]): Observable<GroupViewModel[]> {
    return this.httpClient.post<GroupViewModel[]>(`${this.apiUrl}/group/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentGroups = [...this.groupsSubject.value];
        currentGroups = currentGroups.filter(x => !deletedIds.includes(x.id));
        this.groupsSubject.next(currentGroups);

        return res;
      })
    );
  }

  updateIndustries(industries: IndustryViewModel[]): void {
    const currentIndustries = [...this.industrySubject.value];
    industries.forEach(indsutry => {
      this.updateListValues(indsutry, currentIndustries);
    });
    this.industrySubject.next(currentIndustries);
  }

  getLatestVersion(): Observable<VmImage> {
    return this.httpClient.get<VmImage>(`${this.apiUrl}/latestversion`).pipe(
      map(res => {
        this.latestVersionSubject.next(res);
        return res;
      })
    );
  }

  addMacrosector(value: MacrosectorViewModel): Observable<MacrosectorViewModel> {
    return this.httpClient.post<MacrosectorViewModel>(`${this.apiUrl}/macrosector`, value).pipe(
      map(res => {
        this.updateMetadata({
          macrosector: res
        });

        return res;
      })
    );
  }

  deleteMacrosectors(ids: number[]): Observable<MacrosectorViewModel[]> {
    return this.httpClient.post<MacrosectorViewModel[]>(`${this.apiUrl}/macrosector/delete`, ids).pipe(
      map(res => {
        const deletedIds = res.map(x => x.id);
        let currentMacrosectors = [...this.macrosectorSubject.value];
        currentMacrosectors = currentMacrosectors.filter(x => !deletedIds.includes(x.id));
        this.macrosectorSubject.next(currentMacrosectors);

        let currentDomains = [...this.domainSubject.value];
        currentDomains = currentDomains.filter(x => !deletedIds.includes(x.macrosectorId));
        this.domainSubject.next(currentDomains);

        let currentProcesses = [...this.processesSubject.value];
        currentProcesses = currentProcesses.filter(x => !deletedIds.includes(x.macrosectorId));
        this.processesSubject.next(currentProcesses);

        let currentUsecases = [...this.useCasesSubject.value];
        currentUsecases = currentUsecases.filter(x => !deletedIds.includes(x.macrosectorId));
        this.useCasesSubject.next(currentUsecases);

        return res;
      })
    );
  }

  private updateMetadata(updateMetadata: MetadataHierarchyUpdate): void {
    const currentMacrosectors = [...this.macrosectorSubject.value];
    const currentDomains = [...this.domainSubject.value];
    const currentProcesses = [...this.processesSubject.value];
    const currentUsecases = [...this.useCasesSubject.value];

    if (updateMetadata.macrosector) {
      this.updateListValues(updateMetadata.macrosector, currentMacrosectors);
    }

    if (updateMetadata.domain) {
      this.updateListValues(updateMetadata.domain, currentDomains);

      currentProcesses.forEach(process => {
        if (process.domainId === updateMetadata.domain!.id) {
          process.macrosectorId = updateMetadata.domain!.macrosectorId;
        }
      });

      currentUsecases.forEach(useCase => {
        if (useCase.domainId === updateMetadata.domain!.id) {
          useCase.macrosectorId = updateMetadata.domain!.macrosectorId;
        }
      });
    }

    if (updateMetadata.process) {
      this.updateListValues(updateMetadata.process, currentProcesses);

      currentUsecases.forEach(useCase => {
        if (useCase.processId === updateMetadata.process!.id) {
          useCase.domainId = updateMetadata.process!.domainId;
          useCase.macrosectorId = updateMetadata.process!.macrosectorId;
        }
      });
    }

    if (updateMetadata.useCase) {
      this.updateListValues(updateMetadata.useCase, currentUsecases);
    }

    this.macrosectorSubject.next(currentMacrosectors);
    this.domainSubject.next(currentDomains);
    this.processesSubject.next(currentProcesses);
    this.useCasesSubject.next(currentUsecases);
  }

  private updateListValues<T extends { id: number }>(newValue: T, values: T[]): void {
    const fIndex = values.findIndex(f => f.id === newValue.id);
    if (fIndex > -1) {
      values[fIndex] = newValue;
    } else {
      values.push(newValue);
    }
  }
}
